/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1793577600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 02 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "superchat",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Superchat",
          "depth": 0,
          "id": "connect-to-superchat"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Contacts",
          "depth": 1,
          "id": "contacts"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://www.superchat.com/",
          children: "Superchat"
        }), " is a customer messaging platform that brings WhatsApp, Instagram, SMS, email, and live chat into one shared inbox. With the Superchat destination, you can sync contacts and their custom attributes from your warehouse, and keep Superchat contact lists matched to your audiences so broadcasts and automations reach the right people."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#contacts",
                children: _jsx(_components.strong, {
                  children: "Contacts"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update Superchat contacts and their custom attributes, and keep contact lists up to date"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.superchat.com/reference/contacts",
                children: "Create and update contacts ↗"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-superchat",
        children: "Connect to Superchat"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch connects to Superchat with a workspace API key. You must be signed in to Superchat as an administrator to view it."
      }), "\n", _jsxs(_components.p, {
        children: ["In Superchat, go to ", _jsx(_components.a, {
          href: "https://app.superchat.de/settings/integrations",
          children: _jsx(_components.strong, {
            children: "Settings > Integrations > API"
          })
        }), " and copy the API key. Enter it as the ", _jsx(_components.strong, {
          children: "API key"
        }), " when you set up the destination in Hightouch. The key has read and write access to the whole Superchat workspace."]
      }), "\n", _jsx(_components.p, {
        children: "When you test the connection, Hightouch reads your custom attributes and contact lists to confirm the key works. If the test reports that Superchat rejected the API key, check the key and confirm that API access is included in your Superchat plan."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "contacts",
        children: "Contacts"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " when Hightouch should create missing contacts and update existing ones. Use ", _jsx(_components.strong, {
          children: "Update"
        }), " when Hightouch should only change contacts that already exist in Superchat. Hightouch rejects rows without a matching contact. Use ", _jsx(_components.strong, {
          children: "Insert"
        }), " to create a new contact for every row without looking for an existing one. Superchat rejects a new contact whose email or phone number already belongs to another contact, so use Insert only when your rows are new to Superchat."]
      }), "\n", _jsx(_components.h4, {
        id: "add-contacts-to-a-contact-list",
        children: "Add contacts to a contact list"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Create a new contact list"
        }), " or ", _jsx(_components.strong, {
          children: "Use an existing contact list"
        }), " to add every synced contact to a Superchat contact list, for example to target an audience with a broadcast. Select ", _jsx(_components.strong, {
          children: "Don't add to contact list"
        }), " to only create and update contacts."]
      }), "\n", _jsxs(_components.p, {
        children: ["When you create a new list, Hightouch creates it on the first sync run with the name you enter, or your model's name if you leave it blank. If a list with exactly that name already exists, the sync fails with an error instead of reusing it. Select the list with ", _jsx(_components.strong, {
          children: "Use an existing contact list"
        }), ", or enter a different name."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch adds each contact to the list every time it syncs the contact, and adding a contact that's already on the list has no effect. To remove contacts when they leave your model, see ", _jsx(_components.a, {
          href: "#configure-delete-behavior",
          children: "Configure delete behavior"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In Upsert and Update modes, Hightouch matches rows to Superchat contacts by ", _jsx(_components.strong, {
          children: "Email"
        }), " or ", _jsx(_components.strong, {
          children: "Phone"
        }), ". Superchat matches these values exactly, so Hightouch formats them the way Superchat stores them before searching:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Emails are trimmed and lowercased."
        }), "\n", _jsxs(_components.li, {
          children: ["Phone numbers are converted to E.164 format, such as ", _jsx(_components.code, {
            children: "+447700900123"
          }), ". Numbers without a country code use the default country you select."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Superchat requires every email and phone number to be unique across the workspace, so each value matches at most one contact."
      }), "\n", _jsx(_components.h4, {
        id: "map-fields",
        children: "Map fields"
      }), "\n", _jsxs(_components.p, {
        children: ["Map ", _jsx(_components.strong, {
          children: "Email"
        }), ", ", _jsx(_components.strong, {
          children: "Phone"
        }), ", or both. A new contact needs at least one of them, and Hightouch rejects rows that have neither. You can also map ", _jsx(_components.strong, {
          children: "First name"
        }), ", ", _jsx(_components.strong, {
          children: "Last name"
        }), ", and ", _jsx(_components.strong, {
          children: "Gender"
        }), ". Gender accepts ", _jsx(_components.code, {
          children: "male"
        }), ", ", _jsx(_components.code, {
          children: "female"
        }), ", ", _jsx(_components.code, {
          children: "diverse"
        }), ", or ", _jsx(_components.code, {
          children: "unknown"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Superchat stores a contact's emails and phone numbers as a list. When a synced email or phone number differs from what the contact already has, Hightouch adds it to the contact and keeps the existing ones. Hightouch doesn't remove an email or phone number from a contact."
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Which custom attributes do you want to sync?"
        }), ", select from your Superchat contact attributes. Create an attribute in Superchat before syncing to it. Read-only attributes, which only a Superchat integration can set, aren't listed. Hightouch converts each value to the attribute's type and rejects rows whose value doesn't fit:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Attribute type"
            }), _jsx(_components.th, {
              children: "Expected value"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Text"
            }), _jsx(_components.td, {
              children: "Any value. Objects and arrays are sent as JSON text."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "A number, or text that contains only a number."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Date"
            }), _jsxs(_components.td, {
              children: ["A date in ", _jsx(_components.code, {
                children: "YYYY-MM-DD"
              }), " format. Any time portion is dropped."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Date and time"
            }), _jsx(_components.td, {
              children: "An ISO 8601 date and time. Hightouch sends it in UTC."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Single select"
            }), _jsx(_components.td, {
              children: "One of the attribute's existing options. Superchat rejects values it doesn't have."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Multi select"
            }), _jsx(_components.td, {
              children: "An array of option values, or text containing a JSON array."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Superchat clears an attribute that receives a value of the wrong type instead of returning an error, so Hightouch checks values before sending them."
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.code, {
          children: "NULL"
        }), " custom attribute value clears that attribute on the contact. Superchat ignores empty names and genders, so a ", _jsx(_components.code, {
          children: "NULL"
        }), " ", _jsx(_components.strong, {
          children: "First name"
        }), ", ", _jsx(_components.strong, {
          children: "Last name"
        }), ", or ", _jsx(_components.strong, {
          children: "Gender"
        }), " leaves the existing value unchanged."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-phone-number-formatting",
        children: "Configure phone number formatting"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch asks for a default country when ", _jsx(_components.strong, {
          children: "Phone"
        }), " is your match key or a mapped field. Superchat only accepts phone numbers in E.164 format, so Hightouch converts every phone number before syncing and uses the default country for numbers that don't include a country code. You can select one country for all rows or a column that holds each row's country. A number Hightouch can't parse is sent as it is, and Superchat rejects the row."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "Select what happens to contacts that leave your model. Hightouch never deletes Superchat contacts. Delete behavior isn't available in Insert mode."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Remove the Superchat record from the specified audience"
          }), " — Removes the contact from the sync's contact list. The contact, its attributes, and its other list memberships stay in Superchat. This is the default when the sync adds contacts to a list."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Clear fields that are being synced to in Superchat record, but do not delete"
          }), " — Clears the mapped custom attributes on the contact. Names, gender, emails, and phone numbers aren't cleared, and the contact stays on the contact list."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Do nothing"
          }), " — Leaves the contact unchanged. This is the default when the sync doesn't use a contact list."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Superchat, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nOther destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Large syncs are slow because of Superchat's rate limit."
          }), " Superchat allows 2,500 API requests per 5 minutes for each workspace, shared by every API key and integration in that workspace. Superchat has no batch endpoints, so each contact takes two to three requests: a search, a create or update, and a list addition when the sync uses a list. Hightouch paces its requests to stay under the limit, which puts a first sync of 100,000 contacts at roughly eight hours. Later runs only send rows that changed in your model, and Hightouch skips the update when a contact already has the synced values."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contacts collect emails and phone numbers."
          }), " Because Hightouch adds new emails and phone numbers without removing old ones, a contact whose email changes in your warehouse keeps both addresses in Superchat."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "An email or phone number can belong to only one contact."
          }), " If a synced email or phone number already belongs to a different contact, Superchat rejects the row."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, search for a synced contact in Superchat by email or phone number, and confirm its name and custom attributes match your model. If the sync uses a contact list, open the list in Superchat and confirm the synced contacts appear on it."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
